import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FaCog, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import CustomFieldsService from '../../../../../../../AppSettings/services/CustomfieldsService';
import { useParams } from 'react-router-dom';
import { CustomField } from '@frontend/Models/CustomField/CustomFieldModels';
import { CustomValue } from '@frontend/Models/CustomValue/CustomValueModels';
import './MatterCustomFields.css';
import FormEntryMultilineTextbox from '@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox';

interface MatterCustomFieldsProps {}

const MatterCustomFields: React.FC<MatterCustomFieldsProps> = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const queryClient = useQueryClient();
  const [editingField, setEditingField] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string | boolean>('');

  // Query for fetching custom fields and their values
  const { data: customFieldsData, isLoading } = useQuery<CustomField[]>({
    queryKey: ['matterCustomFields', matterId],
    queryFn: async (): Promise<CustomField[]> => {
      if (!matterId) throw new Error('Matter ID is required');

      const [fieldsResponse, valuesResponse] = await Promise.all([
        CustomFieldsService.getMatterCustomFields(matterId),
        CustomFieldsService.getMatterCustomValues(matterId),
      ]);

      // TODO: Ben, Is this right
      // Type the responses properly
      const fields: any[] = (fieldsResponse as any).data?.custom_fields || [];
      const customValues: CustomValue[] =
        (valuesResponse as any).data?.custom_values || [];

      // Merge the field definitions with their values
      return fields.map((field): CustomField => {
        const matchedValue = customValues.find(
          (v) => v.customfield_id === field.customfield_id
        );
        return {
          ...field,
          vh_id: matchedValue?.vh_id,
          value_assoc: matchedValue?.value_assoc || '',
        };
      });
    },
    enabled: !!matterId,
  });

  // Mutation for updating custom field values
  const updateCustomValue = useMutation({
    mutationFn: async ({
      vh_id,
      customfield_id,
      value_assoc,
    }: {
      vh_id?: string;
      customfield_id: number;
      value_assoc: string;
    }) => {
      if (!matterId) throw new Error('Matter ID is required');

      if (vh_id) {
        // Update existing value
        return CustomFieldsService.updateCustomValue(vh_id, {
          value_assoc,
          customfield_id,
          matterid: matterId,
        });
      } else {
        // Create new value
        return CustomFieldsService.createCustomValue({
          value_assoc,
          customfield_id,
          matterid: matterId,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['matterCustomFields', matterId],
      });
      setEditingField(null);
      setEditValue('');
    },
  });

  // Normalize backend stored types to UI control types
  const normalizeFieldType = (rawType: string | undefined): string => {
    if (!rawType) return 'text';
    const type = String(rawType).toLowerCase();
    if (type.startsWith('numeric')) return 'number';
    const map: Record<string, string> = {
      varchar: 'text',
      text: 'text',
      integer: 'number',
      int: 'number',
      decimal: 'number',
      float: 'number',
      boolean: 'boolean',
      bool: 'boolean',
      date: 'date',
      timestamp: 'date',
      email: 'email',
      phone: 'tel',
      url: 'url',
      textarea: 'textarea',
      matter_type: 'text',
    };
    return map[type] || type;
  };

  const handleEdit = (field: CustomField): void => {
    setEditingField(field.customfield_id);
    const normalizedType = normalizeFieldType(field.type);
    if (normalizedType === 'boolean') {
      setEditValue(String(field.value_assoc).toLowerCase() === 'true');
    } else {
      setEditValue(field.value_assoc ?? '');
    }
  };

  const handleSave = async (field: CustomField): Promise<void> => {
    const normalizedType = normalizeFieldType(field.type);
    let valueToSave: string = editValue as string;
    if (normalizedType === 'boolean') {
      valueToSave = editValue ? 'true' : 'false';
    } else if (typeof valueToSave === 'string') {
      valueToSave = valueToSave.trim();
    }

    if (typeof valueToSave === 'string' && valueToSave.length === 0) {
      setEditingField(null);
      setEditValue('');
      return;
    }

    await updateCustomValue.mutateAsync({
      vh_id: field.vh_id,
      customfield_id: field.customfield_id,
      value_assoc: valueToSave,
    });
  };

  const handleCancel = (): void => {
    setEditingField(null);
    setEditValue('');
  };

  // Get abbreviation (first letter of each word)
  const getAbbreviation = (name: string): string => {
    return name
      .split(' ')
      .map((word) => word[0]?.toUpperCase() || '')
      .join('');
  };

  // Define table columns
  return (
    <div className="MatterCustomFields">
      <div className="stakeholder-section-header">
        <FaCog style={{ marginRight: 8 }} />
        <h3 style={{ margin: 0 }}>Custom Fields</h3>
      </div>

      {isLoading ? (
        <div>Loading...</div>
      ) : !customFieldsData || customFieldsData.length === 0 ? (
        <div>No custom fields found</div>
      ) : (
        <table className="Widget-Table">
          <thead>
            <tr>
              <th style={{ width: '30%' }}>Field Name</th>
              <th style={{ width: '50%' }}>Value</th>
              <th style={{ width: '20%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customFieldsData.map((field) => (
              <tr key={field.customfield_id}>
                <td>
                  <div className="field-name-container">
                    <div className="field-avatar">
                      {getAbbreviation(field.custom_field)}
                    </div>
                    <span>{field.custom_field}</span>
                  </div>
                </td>
                <td>
                  {editingField === field.customfield_id ? (
                    (() => {
                      const controlType = normalizeFieldType(field.type);
                      if (controlType === 'textarea') {
                        return (
                          <FormEntryMultilineTextbox
                            name={`customfield_${field.customfield_id}`}
                            className="custom-field-textarea"
                            value={typeof editValue === 'string' ? editValue : ''}
                            onChange={(e) => setEditValue((e.target as HTMLTextAreaElement).value)}
                            placeholder="Enter value"
                            autoFocus
                            rows={3}
                          />
                        );
                      }
                      if (controlType === 'boolean') {
                        return (
                          <input
                            className="custom-field-checkbox"
                            type="checkbox"
                            checked={!!editValue}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const nextVal = e.target.checked;
                              setEditValue(nextVal);
                              updateCustomValue.mutate({
                                vh_id: field.vh_id,
                                customfield_id: field.customfield_id,
                                value_assoc: nextVal ? 'true' : 'false',
                              });
                            }}
                          />
                        );
                      }
                      const inputType: string = [
                        'number',
                        'date',
                        'email',
                        'tel',
                        'url',
                      ].includes(controlType)
                        ? controlType
                        : 'text';
                      return (
                        <input
                          className="custom-field-input"
                          type={inputType as React.HTMLInputTypeAttribute}
                          value={typeof editValue === 'string' ? editValue : ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setEditValue(e.target.value)
                          }
                          placeholder="Enter value"
                          autoFocus
                        />
                      );
                    })()
                  ) : (
                    <span>{field.value_assoc || '-'}</span>
                  )}
                </td>
                <td>
                  <div className="actions-container">
                    {editingField === field.customfield_id ? (
                      <>
                        <button
                          className="action-button save-button"
                          onClick={() => handleSave(field)}
                          disabled={updateCustomValue.isPending}
                          title="Save"
                        >
                          <FaSave size={14} />
                        </button>
                        <button
                          className="action-button cancel-button"
                          onClick={handleCancel}
                          title="Cancel"
                        >
                          <FaTimes size={14} />
                        </button>
                      </>
                    ) : (
                      <button
                        className="action-button edit-button"
                        onClick={() => handleEdit(field)}
                        title="Edit"
                      >
                        <FaEdit size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MatterCustomFields;
